import type { Maybe } from '@adornis/base/utilTypes.js';
import { TextExtension } from '@adornis/buildify/client/extensions/TextExtension.js';
import type { BuildifyExtension } from '@adornis/buildify/client/globals/types.js';
import { BaseH3 } from '@adornis/buildify/db/extensions/text/BaseH3.js';
import { BaseText } from '@adornis/buildify/db/extensions/text/BaseText.js';
import { type BuildifyGlobalSettings, subscribeBuildifyGlobalSettings } from '@adornis/buildify/db/GlobalSettings.js';
import { BuildifyCampusContainerFlexBackgroundExtension } from '@adornis/digitale-helden-shared/client/theme/buildify/extensions/container/flex/BuildifyCampusContainerFlexBackgroundExtension.js';
import { BuildifyCampusContainerFlexLineExtension } from '@adornis/digitale-helden-shared/client/theme/buildify/extensions/container/flex/BuildifyCampusContainerFlexLineExtension.js';
import {
  buildifyDefaultTiptapExtensions,
  customParagraph,
  getDefaultDubniumBuildifyExtensions,
} from '@adornis/digitale-helden-shared/client/theme/buildify/helpers.js';
import { DigitaleHeldenCharCount } from '@adornis/digitale-helden-shared/client/theme/tiptap/d-tiptap.js';
import { MarketingBuildifyGlobalSettings } from '@adornis/digitale-helden-shared/db/buildify/DigitaleHeldenBuildifyGlobalSettings.js';
import { DubniumBaseTag } from '@adornis/digitale-helden-shared/db/buildify/DubniumBaseTag.js';
import { MarketingCarouselExtension } from './carousel-extension/MarketingCarouselExtension.js';
import { MarketingBetterPlace } from './MarketingBetterPlace.js';
import { MarketingBookingFrame } from './MarketingBookingFrame.js';
import { MarketingFullsizeContainerExtension } from './MarketingFullsizeContainerExtension.js';
import { MarketingInstagramFeed } from './MarketingInstagramFeed.js';
import { MarketingLandingSearchExtension } from './MarketingLandingSearchExtension.js';
import { MarketingSectionExtension } from './MarketingSectionExtension.js';
import { MarketingSliderExtension } from './MarketingSliderExtension.js';
import { MarketingTypingAnimationExtension } from './MarketingTypingAnimationExtension.js';

export const getMarketingBuildifyExtensions = async () => {
  const globalSettings = await new Promise<Maybe<BuildifyGlobalSettings>>((res, rej) => {
    subscribeBuildifyGlobalSettings(MarketingBuildifyGlobalSettings._class)({
      colors: 1,
    }).subscribe(settings => {
      if (!settings) return rej('settings not found');
      return res(settings as BuildifyGlobalSettings);
    });
  });
  if (!globalSettings) return [];

  const extensions = await getDefaultDubniumBuildifyExtensions({
    sectionExtension: MarketingSectionExtension,
    globalSettingsClassName: MarketingBuildifyGlobalSettings._class,
  });
  const textExtension = extensions.find(ext => ext.class._class === BaseText._class);
  const tagExtension = extensions.find(ext => ext.class._class === DubniumBaseTag._class);
  const h3Extension = extensions.find(ext => ext.class._class === BaseH3._class);

  if (!textExtension || !tagExtension || !h3Extension) throw new Error('extension not found in default');

  extensions.push(MarketingFullsizeContainerExtension);
  extensions.push(MarketingTypingAnimationExtension);
  extensions.push(MarketingLandingSearchExtension);
  extensions.push(MarketingSliderExtension(textExtension));
  extensions.push(MarketingInstagramFeed);
  extensions.push(MarketingBetterPlace);
  extensions.push(MarketingBookingFrame);

  const removeExtensionAndReplaceWith = (oldExtension: BuildifyExtension, newExtension: BuildifyExtension) => {
    extensions.splice(
      extensions.findIndex(ext => ext.class._class === oldExtension.class._class),
      1,
    );
    extensions.push(newExtension);
  };

  removeExtensionAndReplaceWith(
    BuildifyCampusContainerFlexBackgroundExtension,
    BuildifyCampusContainerFlexBackgroundExtension,
  );
  removeExtensionAndReplaceWith(BuildifyCampusContainerFlexLineExtension, BuildifyCampusContainerFlexLineExtension);

  //   extensions.push(MarketingNavigationLinkExtension);

  // carousel
  const textMaxCharExtension = TextExtension([
    ...buildifyDefaultTiptapExtensions(customParagraph('text'), globalSettings),
    [DigitaleHeldenCharCount],
  ]);
  const smallTextExtension = TextExtension(
    buildifyDefaultTiptapExtensions(customParagraph('text', { fontSize: '14px' }), globalSettings),
  );
  const carouselExtension = MarketingCarouselExtension({
    textMaxCharExtension,
    tagExtension,
    h3Extension,
    smallTextExtension,
  });
  extensions.push(carouselExtension);
  // ### end carousel

  return extensions;
};
